import React from 'react';
import useTabParam from '@lib/hooks/useTabParam';
import { AccessComponentEnum, TaskScopeNameEnum } from 'graphql-common';
import { GetUrlParams, ORDERING_DIRECTION_ASC } from '@lib/enums/urls';
import usePrevious from '@lib/hooks/usePrevious';
import { TaskScopeNameEnumExtended } from 'constants/enums';
import { APP_URLS } from 'constants/urls';
import Tasks from './Tasks';
import useActions from './utils/useActions';

function TasksContainer() {
  // During navigate() to another route, `use-query-params` re-renders this
  // container with the destination route's URL params *before* React Router
  // commits the unmount (its setState-driven context lags one render).
  // `useMatch` / `useLocation` read from that lagging context, but
  // `use-query-params` reads `window.location` directly via its
  // ReactRouter6Adapter (`navigator.location`), so the in-flight variables
  // already reflect the new URL while the React Router match still reports
  // the old one. Use `window.location.pathname` here so the gate sees the
  // same value `useQueryParams` does, and the query is skipped during the
  // transition render.
  const isRouteMatched =
    typeof window !== 'undefined' &&
    window.location.pathname === APP_URLS.app.tasks.index.path;

  const [tabParam, setTabParam] = useTabParam({
    defaultTab: TaskScopeNameEnum.MyTodoAvailableToComplete,
    defaultPaginationParams: {
      [GetUrlParams.OrderingField]: 'deadlineAt',
      [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_ASC,
    },
  });
  const prevTabParam = usePrevious(tabParam);

  const {
    downloadsData,
    downloadsLimitValue,
    downloadsLoading,
    downloadsTotalCount,
    taskCounterData,
    tasks,
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    totalTasksCount,
  } = useActions({ tabParam, skip: !isRouteMatched });

  return (
    <Tasks
      downloadsData={downloadsData}
      downloadsLimitValue={downloadsLimitValue}
      downloadsLoading={downloadsLoading}
      downloadsTotalCount={downloadsTotalCount}
      onTabParamChange={setTabParam}
      tabParam={tabParam as TaskScopeNameEnumExtended}
      prevTabParam={prevTabParam as TaskScopeNameEnumExtended}
      taskCounterData={taskCounterData}
      tasks={tasks}
      tasksError={tasksError}
      tasksFirstLoading={tasksFirstLoading}
      tasksLoading={tasksLoading}
      totalEntries={totalTasksCount}
    />
  );
}

TasksContainer.displayName = `${AccessComponentEnum.TasksPerforming}-TasksContainer`;

export default TasksContainer;
